import {
	BoundingSphere,
	Cartesian3,
	Cartographic,
	JulianDate,
	Color,
	PolygonHierarchy,
	ClassificationType,
	Ellipsoid,
	Matrix4,
	Transforms,
	defined,
	LabelStyle,
	HeightReference,
	Cartesian2,
	VerticalOrigin,
	HorizontalOrigin,
	DistanceDisplayCondition,
	NearFarScalar,
} from 'cesium';
import { getLanguage, t } from '@/i18n';
import { numberLocaleFor } from '@/i18n/format';
import { getDamageViewOffset } from './damageView';

export const COLOR_POINT_NORMAL = '#FFFF00';
export const COLOR_POINT_SELECTED = '#00FFFF';

export const COLOR_POLYGON_NORMAL = '#FFFF00'; // yellow
export const COLOR_POLYGON_SELECTED = '#00FFFF'; // red
export const COLOR_UPLOAD_SELECTED = '#16a34a';

export const POLYGON_ALPHA_NORMAL = 0.6;
export const POLYGON_ALPHA_SELECTED = 0.5;

// The attachment icon hides almost immediately upon zooming out
const MAX_ICON_VISIBLE_DISTANCE = 200.0;
// The main damage pin hides when zoomed out very far
const MAX_PIN_VISIBLE_DISTANCE = 500.0;

// ==========================================
// CALCULATION & MATH HELPERS
// ==========================================

const formatArea = (area, unit = 'sqft') => {
	const unitsMap = {
		m2: 'm²',
		sqft: 'sqft',
	};

	if (!unitsMap[unit]) {
		console.error("Unsupported unit. Use 'm2' or 'sqft'.");
		return t('viewer.measure.invalidUnit');
	}

	return `${area.toLocaleString(numberLocaleFor(getLanguage()), {
		maximumFractionDigits: 2,
	})} ${unitsMap[unit]}`;
};

/**
 * Calculates the surface area of a set of Cartesian3 positions in 3D space.
 * Works for Ground, Walls, and Roofs.
 * @param {Cartesian3[]} cartesianPositions
 * @param {Object} options
 * @returns {number|string}
 */
export const calculatePolygonArea = (
	cartesianPositions,
	{ unit = 'm2', formatted = true } = {}
) => {
	if (!cartesianPositions || cartesianPositions.length < 3) {
		console.warn('At least 3 points are required to form a polygon.');
		return formatted ? '0 ' + (unit === 'm2' ? 'm²' : 'sqft') : 0;
	}

	// We compute the area of triangles connecting P0 -> Pi -> Pi+1
	// This avoids projecting to a specific 2D plane, handling vertical walls automatically.
	const origin = cartesianPositions[0];
	let areaVector = new Cartesian3(0, 0, 0);

	const v1 = new Cartesian3();
	const v2 = new Cartesian3();
	const cross = new Cartesian3();

	for (let i = 1; i < cartesianPositions.length - 1; i++) {
		const p1 = cartesianPositions[i];
		const p2 = cartesianPositions[i + 1];

		Cartesian3.subtract(p1, origin, v1);
		Cartesian3.subtract(p2, origin, v2);
		Cartesian3.cross(v1, v2, cross);
		Cartesian3.add(areaVector, cross, areaVector);
	}

	let area = Cartesian3.magnitude(areaVector) * 0.5;

	const conversions = {
		m2: (a) => a,
		sqft: (a) => a * 10.7639,
	};

	if (!conversions[unit]) {
		throw new Error(`Unsupported unit "${unit}". Use "m2" or "sqft".`);
	}

	const convertedArea = conversions[unit](area);
	return formatted ? formatArea(convertedArea, unit) : convertedArea;
};

/**
 * Calculates the perimeter (total length) of a set of Cartesian3 positions.
 * Distance is computed from point to point around the polygon.
 * @param {Cartesian3[]} cartesianPositions
 * @param {Object} options
 * @returns {number|string}
 */
export const calculatePolygonPerimeter = (
	cartesianPositions,
	{ unit = 'cm', formatted = true } = {}
) => {
	if (!cartesianPositions || cartesianPositions.length < 2) {
		return formatted ? `0 ${unit}` : 0;
	}

	let perimeter = 0;

	// Loop through all points and calculate distance to the next point
	// The modulo (%) handles the distance from the last point back to the first point
	for (let i = 0; i < cartesianPositions.length; i++) {
		const p1 = cartesianPositions[i];
		const p2 = cartesianPositions[(i + 1) % cartesianPositions.length];
		perimeter += Cartesian3.distance(p1, p2);
	}

	// Cesium Cartesian3 distance is natively in meters
	const conversions = {
		m: (p) => p,
		cm: (p) => p * 100,
	};

	if (!conversions[unit]) {
		throw new Error(`Unsupported unit "${unit}". Use "m" or "cm".`);
	}

	const convertedPerimeter = conversions[unit](perimeter);
	return formatted
		? `${convertedPerimeter.toLocaleString(numberLocaleFor(getLanguage()), { maximumFractionDigits: 2 })} ${unit}`
		: convertedPerimeter;
};

/**
 * Finds an entity by damageId. If not found, looks for a 'new-polygon-' entity.
 * Calculates and returns the area.
 * @param {Cesium.Viewer} viewer
 * @param {string} damageId
 */
export const getDamagePolygonArea = (viewer, damageId) => {
	let entity = findDamagePolygon(viewer, damageId);

	// Fallback: If no saved entity found, look for a draft (new) polygon
	if (!entity || !entity.polygon) {
		entity = findNewPolygon(viewer);
	}

	if (!entity || !entity.polygon) {
		console.warn(`No valid polygon entity found for Damage ID: ${damageId} or new drafts.`);
		return null;
	}

	const currentTime = viewer.clock.currentTime;
	const hierarchy = entity.polygon.hierarchy.getValue(currentTime);

	if (!hierarchy || !hierarchy.positions) {
		return null;
	}

	const calculatedArea = calculatePolygonArea(hierarchy.positions, {
		unit: 'm2',
		formatted: false,
	});

	return calculatedArea ? calculatedArea.toFixed(2) : null;
};

/**
 * Finds an entity by damageId. If not found, looks for a 'new-polygon-' entity.
 * Calculates and returns the perimeter.
 * @param {Cesium.Viewer} viewer
 * @param {string} damageId
 */
export const getDamagePolygonPerimeter = (viewer, damageId) => {
	let entity = findDamagePolygon(viewer, damageId);

	// Fallback: If no saved entity found, look for a draft (new) polygon
	if (!entity || !entity.polygon) {
		entity = findNewPolygon(viewer);
	}

	if (!entity || !entity.polygon) {
		console.warn(`No valid polygon entity found for Damage ID: ${damageId} or new drafts.`);
		return null;
	}

	const currentTime = viewer.clock.currentTime;
	const hierarchy = entity.polygon.hierarchy.getValue(currentTime);

	if (!hierarchy || !hierarchy.positions) {
		return null;
	}

	const calculatedPerimeter = calculatePolygonPerimeter(hierarchy.positions, {
		unit: 'cm',
		formatted: false,
	});

	return calculatedPerimeter ? calculatedPerimeter.toFixed(2) : null;
};

/**
 * Loads multiple polygons onto the map, ensuring they sit on top of 3D tiles.
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Array} polygonsData - Array of [[lon, lat], ...] arrays
 */
export const loadPolygons = async (viewer, polygonsData) => {
	if (!viewer) return;

	const COLOR_POLYGON_NORMAL_CESIUM =
		Color.fromCssColorString(COLOR_POLYGON_NORMAL).withAlpha(POLYGON_ALPHA_NORMAL);

	const HEIGHT_OFFSET = 0.05;

	clearDamageEntities(viewer);

	for (const data of polygonsData) {
		const geometry = data.polygon?.coordinates;

		if (!geometry) {
			console.warn(`Geometry ${data.id} is missing. Skipping.`);
			continue;
		}

		const geometryType = getGeometryType(geometry);

		if (geometryType === 'point') {
			addPointToMap(viewer, geometry, data);
			continue;
		}

		if (geometryType !== 'polygon') {
			console.warn(`Unknown geometry type for ${data.id}`);
			continue;
		}
		const coords = geometry;

		let finalPositions = [];
		let useDraping = false;
		const hasExplicitHeight = coords[0].length > 2;

		const labelPosition = getPolygonCenter(coords);
		const fileCount = data.uploadsCount || 0;

		if (hasExplicitHeight) {
			// SCENARIO 1: WALL/BRIDGE
			// We trust the saved data completely. Do NOT sample height.
			finalPositions = coords.map((pos) => Cartesian3.fromDegrees(pos[0], pos[1], pos[2]));
		} else {
			// SCENARIO 2: GROUND
			// No height data, so we must calculate ground height.
			const cartographics = coords.map((pos) => Cartographic.fromDegrees(pos[0], pos[1]));

			try {
				const updatedCartographics = await viewer.scene.sampleHeightMostDetailed(cartographics);
				const validSample = updatedCartographics.every((c) => c.height > -1000);

				if (validSample) {
					finalPositions = updatedCartographics.map((c) =>
						Cartesian3.fromRadians(c.longitude, c.latitude, c.height + HEIGHT_OFFSET)
					);
				} else {
					useDraping = true;
				}
			} catch (error) {
				console.warn(`Polygon ${data.id}: Sampling failed. Falling back to Draping.`);
				useDraping = true;
			}
		}

		const entityOptions = {
			name: 'damage-polygon',
			id: data.id,
			type: 'DamagePolygon',
			position: labelPosition, // Bounding sphere center
			properties: {
				rawPositions: coords,
				damageId: data.damageId,
				inspectionId: data.inspectionId,
				assetId: data.assetId,
				uploadsCount: fileCount,
			},
		};

		// Swap Label for the Attachment Billboard
		if (fileCount > 0) {
			entityOptions.billboard = {
				image: createAttachmentIcon(),
				scale: 1.15, // Made larger
				scaleByDistance: new NearFarScalar(100, 1.15, 5000, 0.2),
				verticalOrigin: VerticalOrigin.BOTTOM,
				horizontalOrigin: HorizontalOrigin.LEFT,
				pixelOffset: new Cartesian2(0, 0),
				// pixelOffsetScaleByDistance: new NearFarScalar(100, 1.0, 5000, 0.2),
				distanceDisplayCondition: new DistanceDisplayCondition(0.0, MAX_ICON_VISIBLE_DISTANCE),
				heightReference: useDraping ? HeightReference.CLAMP_TO_GROUND : HeightReference.NONE,
			};
		}

		if (useDraping) {
			// Draping only works for "flat" ground data without height
			const flatCoords = coords.map((c) => [c[0], c[1]]).flat();
			entityOptions.polygon = {
				hierarchy: Cartesian3.fromDegreesArray(flatCoords),
				material: COLOR_POLYGON_NORMAL_CESIUM,
				classificationType: ClassificationType.CESIUM_3D_TILE,
				perPositionHeight: false,
			};
		} else {
			entityOptions.polygon = {
				hierarchy: new PolygonHierarchy(finalPositions),
				material: COLOR_POLYGON_NORMAL_CESIUM,
				perPositionHeight: true, // Required for walls
				outline: true,
				outlineColor: Color.BLACK,
				outlineWidth: 2,
			};
		}

		viewer.entities.add(entityOptions);
	}

	viewer.scene.requestRender();
};

export const clearDamageEntities = (viewer) => {
	if (!viewer) return;
	const entities = viewer.entities.values;
	for (let i = entities.length - 1; i >= 0; i--) {
		const entity = entities[i];
		if (
			entity.name === 'damage-polygon' ||
			entity.type === 'DamagePolygon' ||
			entity.type === 'DamagePoint' ||
			entity.name === 'damage-attachment-icon'
		) {
			viewer.entities.remove(entity);
		}
	}
};

/**
 * Highlights a specific damage entity on the map and zooms to it.
 * Resets previously highlighted entities.
 * @param {Cesium.Viewer} viewer
 * @param {string} damageId
 * @returns {boolean} true if found and highlighted, false otherwise
 */
export const highlightDamageOnMap = (viewer, damageId) => {
	if (!viewer) return false;

	const entity = findDamagePolygon(viewer, damageId);
	if (!entity) return false;

	viewer.entities.values.forEach((ent) => {
		if (ent.type === 'DamagePolygon' && ent.polygon) {
			ent.polygon.material =
				Color.fromCssColorString(COLOR_POLYGON_NORMAL).withAlpha(POLYGON_ALPHA_NORMAL);
			ent.polygon.outlineColor = Color.BLACK;
			ent.polygon.outlineWidth = 2;
		}

		if (ent.type === 'DamagePoint' && ent.billboard) {
			ent.billboard.image = createMapPinIcon(COLOR_POINT_NORMAL);
			ent.billboard.scale = 1.5; // Reset to bigger normal size
		}
	});

	if (entity.type === 'DamagePolygon' && entity.polygon) {
		entity.polygon.material =
			Color.fromCssColorString(COLOR_POLYGON_SELECTED).withAlpha(POLYGON_ALPHA_SELECTED);
		entity.polygon.outlineColor = Color.fromCssColorString(COLOR_POLYGON_SELECTED);
		entity.polygon.outlineWidth = 12;
	}

	if (entity.type === 'DamagePoint' && entity.billboard) {
		entity.billboard.image = createMapPinIcon(COLOR_POINT_SELECTED);
		entity.billboard.scale = 2.0; // Highlighted size even bigger
	}

	// The offset waits for the tiles around the damage, so the camera lands on its visible side.
	getDamageViewOffset(viewer, entity)
		.then((offset) => {
			if (viewer.isDestroyed()) return;
			return viewer.flyTo(entity, { duration: 1.5, offset });
		})
		.catch(console.warn);

	return true;
};

export const findDamagePolygon = (viewer, damageId) => {
	if (!viewer) return false;

	return viewer.entities.values.find((ent) => {
		const entDamageId = ent.properties?.damageId?.getValue();
		return entDamageId === damageId;
	});
};

export const findNewPolygon = (viewer) => {
	if (!viewer) return null;

	return viewer.entities.values.find((ent) => {
		if (ent.id && typeof ent.id === 'string') {
			return ent.id.startsWith('new-polygon-');
		}
		return false;
	});
};

function getGeometryType(obj) {
	if (!Array.isArray(obj)) return 'unknown';

	// length > 3 → polygon
	if (obj.length > 3) {
		return 'polygon';
	}

	// length === 3 → inspect first element
	if (obj.length === 3) {
		return Array.isArray(obj[0]) ? 'polygon' : 'point';
	}

	return 'unknown';
}

const addPointToMap = (viewer, coords, data) => {
	const [lon, lat, height = 0] = coords;
	const fileCount = data.uploadsCount || 0;

	// Add Main Map Pin Point
	const entityOptions = {
		name: 'damage-point',
		id: data.id,
		type: 'DamagePoint',
		position: Cartesian3.fromDegrees(lon, lat, height),
		billboard: {
			image: createMapPinIcon(COLOR_POINT_NORMAL),
			scale: 1.5,
			scaleByDistance: new NearFarScalar(100, 1.0, 5000, 0.6),
			verticalOrigin: VerticalOrigin.BOTTOM,
			// Hides the point pin completely when zoomed out enough
			distanceDisplayCondition: new DistanceDisplayCondition(0.0, MAX_PIN_VISIBLE_DISTANCE),
		},
		properties: {
			rawPosition: coords,
			damageId: data.damageId,
			inspectionId: data.inspectionId,
			assetId: data.assetId,
			uploadsCount: fileCount,
		},
	};

	viewer.entities.add(entityOptions);

	// Add Secondary Entity for the Attachment Icon
	if (fileCount > 0) {
		viewer.entities.add({
			name: 'damage-attachment-icon',
			position: Cartesian3.fromDegrees(lon, lat, height),
			billboard: {
				image: createAttachmentIcon(),
				scale: 1.15, // Made larger
				scaleByDistance: new NearFarScalar(100, 1.15, 5000, 0.2),
				verticalOrigin: VerticalOrigin.BOTTOM,
				horizontalOrigin: HorizontalOrigin.LEFT,
				pixelOffset: new Cartesian2(0, -20),
				pixelOffsetScaleByDistance: new NearFarScalar(100, 1.0, 5000, 0.2),
				distanceDisplayCondition: new DistanceDisplayCondition(0.0, MAX_ICON_VISIBLE_DISTANCE),
			},
			properties: { ...entityOptions.properties },
		});
	}
};

// ==========================================
// ICONS & SVG HELPERS
// ==========================================

export const createMapPinIcon = (color = '#ef4444') => {
	const svg = `
	<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
		fill="none" stroke="${color}" stroke-width="2"
		stroke-linecap="round" stroke-linejoin="round">
		<path d="M12 21s-6-5.686-6-10a6 6 0 1 1 12 0c0 4.314-6 10-6 10z"/>
		<circle cx="12" cy="11" r="2"/>
	</svg>`;

	return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

/**
 * Creates an SVG string for an Attachment (Paperclip) Icon inside a blue badge
 */
export const createAttachmentIcon = () => {
	const attachIcon = `
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
		fill="#3b82f6" stroke="#ffffff" stroke-width="2"
		stroke-linecap="round" stroke-linejoin="round">
		<path d="M14.5 9.5L9.25 14.75A1.5 1.5 0 0 1 7.13 12.63L12.38 7.38a3 3 0 0 1 4.24 4.24l-5.25 5.25a4.5 4.5 0 0 1-6.36-6.36l4.95-4.95" />
	</svg>`;

	const cameraIcon = `
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
		fill="#3b82f6" stroke="#ffffff" stroke-width="2"
		stroke-linecap="round" stroke-linejoin="round">
		<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
		<circle cx="12" cy="13" r="4" fill="none"/>
	</svg>`;

	const documentIcon = `
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
		fill="#3b82f6" stroke="#ffffff" stroke-width="2"
		stroke-linecap="round" stroke-linejoin="round">
		<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#3b82f6"/>
		<polyline points="14 2 14 8 20 8" fill="none"/>
		<line x1="16" y1="13" x2="8" y2="13" fill="none"/>
		<line x1="16" y1="17" x2="8" y2="17" fill="none"/>
		<polyline points="10 9 9 9 8 9" fill="none"/>
	</svg>`;

	return `data:image/svg+xml;utf8,${encodeURIComponent(attachIcon)}`;
};

const getPolygonCenter = (coords) => {
	const positions = coords.map((pos) => Cartesian3.fromDegrees(pos[0], pos[1], pos[2] || 0));
	const sphere = BoundingSphere.fromPoints(positions);
	return sphere.center;
};
